import { CheckOutlined, KeyboardArrowDown } from '@mui/icons-material';
import { Button, ButtonProps } from '@mui/material';
import { PropsWithChildren, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';

import { ChatActions } from './ChatActions';
import { HandleActionFn } from './ChatStepActions.hooks';
import { ChatStepAction, ChatStepState } from './ChatStepActions.types';

interface ChatStepActionControllerProps {
  state: ChatStepState;
  handleAction: HandleActionFn<any>;
  disabled: boolean;

  formId?: string;
  skippable?: boolean;
  loading?: boolean;
}

type ActionButtonProps = Omit<ChatStepActionControllerProps, 'state'>;

interface PrimaryActionProps {
  formId?: string;
  handleAction: HandleActionFn<any>;
  stepAction: ChatStepAction.NEXT | ChatStepAction.SAVE;
}

type PrimaryStepActionProps = Omit<ButtonProps, 'type' | 'form' | 'variant' | 'skippable'> & PrimaryActionProps;
const PrimaryStepAction = ({
  formId,
  stepAction,
  handleAction,
  children,
  ...props
}: PropsWithChildren<PrimaryStepActionProps>) => {
  return (
    <Button
      type={formId ? 'submit' : undefined}
      form={formId}
      variant="contained"
      color="primary"
      onClick={() => {
        if (!formId) {
          handleAction(stepAction);
        }
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

const PendingActionButtons = (actionButtonProps: ActionButtonProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'ChatStepAction' });

  const { skippable, ...primaryButtonProps } = actionButtonProps;
  const { handleAction, disabled, loading } = primaryButtonProps;

  return (
    <>
      <PrimaryStepAction
        {...primaryButtonProps}
        stepAction={ChatStepAction.NEXT}
        loadingPosition="end"
        endIcon={<KeyboardArrowDown />}
      >
        {t('next')}
      </PrimaryStepAction>
      {skippable && (
        <Button disabled={disabled || loading} onClick={() => handleAction(ChatStepAction.SKIP)}>
          {t('skip')}
        </Button>
      )}
    </>
  );
};

const ViewActionButtons = ({ handleAction, disabled, loading }: ActionButtonProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'ChatStepAction' });

  return (
    <Button onClick={() => handleAction(ChatStepAction.EDIT)} loading={loading} disabled={disabled}>
      {t('edit')}
    </Button>
  );
};

const EditActionButtons = (actionButtonProps: ActionButtonProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'ChatStepAction' });

  const { skippable, ...primaryButtonProps } = actionButtonProps;
  const { handleAction, disabled } = primaryButtonProps;
  return (
    <>
      <PrimaryStepAction
        {...primaryButtonProps}
        stepAction={ChatStepAction.SAVE}
        loadingPosition="start"
        startIcon={<CheckOutlined />}
      >
        {t('save')}
      </PrimaryStepAction>
      <Button onClick={() => handleAction(ChatStepAction.CANCEL)} disabled={disabled}>
        {t('cancel')}
      </Button>
    </>
  );
};

const STATE_COMPONENT_MAP: Record<ChatStepState, (config: ActionButtonProps) => JSX.Element> = {
  [ChatStepState.PENDING]: PendingActionButtons,
  [ChatStepState.VIEW]: ViewActionButtons,
  [ChatStepState.EDIT]: EditActionButtons,
};

export const ChatStepActions = (controllerProps: ChatStepActionControllerProps) => {
  const { state, ...actionButtonProps } = controllerProps;

  return <ChatActions>{STATE_COMPONENT_MAP[state](actionButtonProps)}</ChatActions>;
};
